<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { IconGitBranch } from '@tabler/icons-vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { CaseLifecycleWorkflowsService, type TCaseLifecycleWorkflow } from '@/types/generated'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectWorkflow: 'Select workflow...',
      noWorkflowsFound: 'No workflows found',
      assignText: 'Assign workflow',
      toTheCase: 'to the case.',
      workflowAssigned: 'The workflow',
      wasAssignedToCase: 'was assigned to the case.'
    },
    de: {
      selectWorkflow: 'Workflow auswählen...',
      noWorkflowsFound: 'Keine Workflows gefunden',
      assignText: 'Setze den Workflow',
      toTheCase: 'für die Anfrage.',
      workflowAssigned: 'Der Workflow',
      wasAssignedToCase: 'wurde der Anfrage zugewiesen.'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const isLoadingWorkflows = ref(false)
  const selectedWorkflowUuid = ref<string>('')
  const workflows = ref<TCaseLifecycleWorkflow[]>([])

  // Computed properties
  const workflowOptions = computed<Option[]>(() => {
    return workflows.value.map((workflow) => ({
      value: workflow.uuid || '',
      title: workflow.name || 'Untitled Workflow',
      icon: undefined
    }))
  })

  const selectedWorkflow = computed(() => {
    return workflows.value.find((workflow) => workflow.uuid === selectedWorkflowUuid.value)
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false && selectedWorkflowUuid.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedWorkflow.value) return ''

    const workflowName = selectedWorkflow.value.name || 'Untitled Workflow'

    return `${t('workflowAssigned')} "${workflowName}" ${t('wasAssignedToCase')}`
  })

  // Methods
  const fetchWorkflows = async () => {
    isLoadingWorkflows.value = true
    try {
      const response = await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsList({
        limit: 100
      })
      workflows.value = response.results || []
    } catch (error) {
      console.error('Error fetching workflows:', error)
      workflows.value = []
    } finally {
      isLoadingWorkflows.value = false
    }
  }

  const handleWorkflowSelection = (workflowUuid: string) => {
    selectedWorkflowUuid.value = workflowUuid
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        workflow_uuid: selectedWorkflowUuid.value
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.workflow_uuid) {
      selectedWorkflowUuid.value = payload.workflow_uuid
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available workflows
    await fetchWorkflows()

    // Emit initial payload if any values are set
    if (selectedWorkflowUuid.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('assignText') }}</span>

        <!-- Workflow Selection -->
        <template v-if="isLoadingWorkflows">
          <SkeletonLoaderInlineField
            width="200px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedWorkflowUuid"
            variant="inline-text"
            :options="workflowOptions"
            :is-filterable="false"
            :disabled="isLoadingWorkflows"
            :placeholder="
              workflowOptions.length === 0 ? t('noWorkflowsFound') : t('selectWorkflow')
            "
            name="workflow"
            @update:model-value="handleWorkflowSelection"
          />
        </template>

        <span>{{ t('toTheCase') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
